import {useNavigate} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {fetchClubs} from "../clients/ClubClient";
import {fetchClubLeaderBoards, fetchGlobalLeaderBoards} from "../clients/LeaderBoardClient";
import {GameVariants, TimeVariants} from "../utils/gameEnums";
import Avatar from "../components/ui/Avatar";
import Icon from "../components/ui/Icon";
import {useUser} from "../UserContext";
import "../LeaderBoards.css";
import usePageTheme from "../utils/usePageTheme";

const VARIANTS = [
    {value: GameVariants.STANDARD, label: "Standard"},
    {value: GameVariants.JUMPSTART, label: "Jumpstart"}
];

const TIMES = [TimeVariants.BULLET, TimeVariants.BLITZ, TimeVariants.RAPID, TimeVariants.CLASSIC];

const GLOBAL = {name: "Everyone", id: null};

const PodiumSpot = ({row, place, onOpen, isMe}) => {
    if (!row) return <div className={`podium__spot podium__spot--${place} podium__spot--empty`}/>;
    return (
        <button type="button" className={`podium__spot podium__spot--${place} ${isMe ? "is-me" : ""}`} onClick={() => onOpen(row)}>
            {place === 1 && <Icon name="crown" className="podium__crown"/>}
            <Avatar name={row.username} size={place === 1 ? 64 : 52}/>
            <div className="podium__name">{row.username}</div>
            <div className="podium__elo">{row.elo}</div>
            <div className="podium__block"><span>{place}</span></div>
        </button>
    );
};

const LeaderBoards = () => {
    usePageTheme("golden");
    const navigate = useNavigate()
    const user = useUser();

    const [pageNo, setPageNo] = useState(1)
    const [gameVariant, setGameVariant] = useState(GameVariants.STANDARD)
    const [timeVariant, setTimeVariant] = useState(TimeVariants.RAPID.value)
    const [clubs, setClubs] = useState([GLOBAL])
    const [selectedClub, setSelectedClub] = useState(GLOBAL)
    const [showClubElos, setShowClubElos] = useState(false)

    const [rows, setRows] = useState([])
    const [lastPage, setLastPage] = useState(true)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!user?.isLoggedIn) return;
        fetchClubs()
            .then((data) => setClubs([GLOBAL, ...data.map((club) => ({name: club.clubName, id: club.clubId}))]))
            .catch(() => setClubs([GLOBAL]));
    }, [user?.isLoggedIn]);

    // Go back to the first page when a filter changes
    useEffect(() => {
        setPageNo(1);
    }, [gameVariant, timeVariant, selectedClub, showClubElos]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        const request = selectedClub.id
            ? fetchClubLeaderBoards(gameVariant, timeVariant, pageNo, selectedClub.id, showClubElos)
            : fetchGlobalLeaderBoards(gameVariant, timeVariant, pageNo);

        request
            .then((data) => {
                if (cancelled) return;
                setRows(data.rows || []);
                setLastPage(data.lastPage);
            })
            .catch((e) => {
                if (cancelled) return;
                console.error("Could not load leaderboards: ", e);
                setRows([]);
                setError("We could not load the leaderboard right now.");
            })
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [gameVariant, timeVariant, selectedClub, showClubElos, pageNo]);

    const openProfile = (row) => {
        if (user?.isLoggedIn && row.username === user.username) navigate("/my-profile");
        else navigate(`/profile?username=${encodeURIComponent(row.username)}`);
    };

    const showPodium = pageNo === 1 && rows.length > 0;
    const podium = showPodium ? rows.slice(0, 3) : [];
    const listRows = showPodium ? rows.slice(3) : rows;
    const timeLabel = TIMES.find((t) => t.value === timeVariant)?.title;

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <div className="eyebrow">Leaderboards</div>
                    <h1>Top players</h1>
                    <p>{VARIANTS.find((v) => v.value === gameVariant)?.label} · {timeLabel}{selectedClub.id ? ` · ${selectedClub.name}` : ""}</p>
                </div>
            </div>

            <div className="card leaderboard-filters">
                <div className="segmented" role="tablist" aria-label="Time control">
                    {TIMES.map((time) => (
                        <button key={time.value} type="button" role="tab" aria-selected={timeVariant === time.value}
                                className={timeVariant === time.value ? "active" : ""}
                                onClick={() => setTimeVariant(time.value)}>
                            <img src={time.imageSrc} alt=""/>{time.title}
                        </button>
                    ))}
                </div>
                <div className="segmented" aria-label="Variant">
                    {VARIANTS.map((variant) => (
                        <button key={variant.value} type="button"
                                className={gameVariant === variant.value ? "active" : ""}
                                onClick={() => setGameVariant(variant.value)}>
                            {variant.label}
                        </button>
                    ))}
                </div>
                {clubs.length > 1 && (
                    <div className="row row--wrap">
                        <select className="select" style={{width: "auto"}} value={selectedClub.name}
                                aria-label="Show players from"
                                onChange={(e) => setSelectedClub(clubs.find((club) => club.name === e.target.value) || GLOBAL)}>
                            {clubs.map((club) => <option key={club.name} value={club.name}>{club.name}</option>)}
                        </select>
                        {selectedClub.id && (
                            <div className="segmented" aria-label="Rating type">
                                <button type="button" className={!showClubElos ? "active" : ""} onClick={() => setShowClubElos(false)}>Global rating</button>
                                <button type="button" className={showClubElos ? "active" : ""} onClick={() => setShowClubElos(true)}>Club rating</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="card card-pad" style={{display: "flex", justifyContent: "center", padding: "3rem"}}>
                    <span className="spinner"/>
                </div>
            ) : error ? (
                <div className="card card-pad"><div className="form-error">{error}</div></div>
            ) : rows.length === 0 ? (
                <div className="card card-pad">
                    <div className="empty-state">
                        <Icon name="trophy"/>
                        <strong>No ratings here yet</strong>
                        <span>Play a {timeLabel?.toLowerCase()} game to be the first on this board.</span>
                    </div>
                </div>
            ) : (
                <>
                    {showPodium && (
                        <div className="podium">
                            <PodiumSpot row={podium[1]} place={2} onOpen={openProfile} isMe={podium[1]?.username === user?.username}/>
                            <PodiumSpot row={podium[0]} place={1} onOpen={openProfile} isMe={podium[0]?.username === user?.username}/>
                            <PodiumSpot row={podium[2]} place={3} onOpen={openProfile} isMe={podium[2]?.username === user?.username}/>
                        </div>
                    )}
                    {listRows.length > 0 && (
                        <div className="card leaderboard-list">
                            {listRows.map((row) => (
                                <button key={row.username} type="button"
                                        className={`leaderboard-row ${row.username === user?.username ? "is-me" : ""}`}
                                        onClick={() => openProfile(row)}>
                                    <span className="leaderboard-row__rank">{row.rank}</span>
                                    <Avatar name={row.username} size={34}/>
                                    <span className="leaderboard-row__name">{row.username}</span>
                                    <span className="leaderboard-row__elo">{row.elo}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}

            {(pageNo > 1 || !lastPage) && (
                <div className="pagination">
                    <button className="btn" onClick={() => setPageNo((p) => Math.max(1, p - 1))} disabled={pageNo <= 1 || loading}>
                        <Icon name="chevronLeft"/> Previous
                    </button>
                    <span className="muted small">Page {pageNo}</span>
                    <button className="btn" onClick={() => setPageNo((p) => p + 1)} disabled={lastPage || loading}>
                        Next <Icon name="chevronRight"/>
                    </button>
                </div>
            )}
        </div>
    );
};

export default LeaderBoards
